import { useRef } from "react";
import {
  Bar, CartesianGrid, Cell, ComposedChart, LabelList,
  ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { MUTED, NAVY } from "../constants";
import { formatQuarterlyMetricValue } from "../formatters";
import { useReferenceAreaBox } from "../hooks/useReferenceAreaBox";
import { HighlightCaption } from "./HighlightCaption";

export function QuarterlyAverageMetricPanel({ metric, height, colors }) {
  const data = Array.isArray(metric?.data) ? metric.data : [];
  const barSeries = (metric?.series || []).find((s) => s.type === "bar") || { key: "value", label: metric?.title };
  const currentIndexes = data.reduce((acc, row, i) => {
    if (row.is_current_quarter) acc.push(i);
    return acc;
  }, []);
  const hasHighlight = currentIndexes.length > 0;
  const highlightRange = hasHighlight
    ? [data[currentIndexes[0]].label, data[currentIndexes[currentIndexes.length - 1]].label]
    : null;
  const formatValue = (value) => formatQuarterlyMetricValue(metric, value);
  const plotLeftInset = 44;
  const plotRightInset = 8;
  const chartWrapRef = useRef(null);
  const highlightBox = useReferenceAreaBox(hasHighlight, chartWrapRef, [data, height]);

  return (
    <div className="flex h-full min-w-0 flex-col">
      <p className="text-[11px] font-black uppercase tracking-[0.14em] mb-2" style={{ color: MUTED }}>
        {metric?.title}
      </p>
      {hasHighlight && (
        <div className="relative" style={{ height: 18 }}>
          <HighlightCaption box={highlightBox} />
        </div>
      )}
      <div ref={chartWrapRef} style={{ height }}>
        {!data.length ? (
          <div className="h-full flex items-center justify-center text-sm" style={{ color: MUTED }}>No data available.</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 24, right: plotRightInset, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e9f2" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: MUTED }}
                axisLine={false}
                tickLine={false}
                minTickGap={12}
              />
              <YAxis tick={{ fontSize: 10, fill: MUTED }} tickFormatter={formatValue} axisLine={false} tickLine={false} width={plotLeftInset} />
              <Tooltip
                contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #d6dfeb" }}
                formatter={(value) => formatValue(value)}
              />
              {highlightRange && (
                <ReferenceArea x1={highlightRange[0]} x2={highlightRange[1]} fill={NAVY} fillOpacity={0.05} stroke={NAVY} strokeOpacity={0.15} />
              )}
              <Bar dataKey={barSeries.key} name={barSeries.label} radius={[4, 4, 0, 0]}>
                {data.map((row, i) => (
                  <Cell key={i} fill={row.is_current_quarter ? colors.dark : colors.light} />
                ))}
                {barSeries.show_value_labels !== false && (
                  <LabelList dataKey={barSeries.key} position="top" formatter={formatValue} style={{ fill: NAVY, fontSize: 10, fontWeight: 700 }} />
                )}
              </Bar>
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
